import React, { useEffect, useMemo, useState } from 'react';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Monitor, Smartphone, Tablet, Pencil, RefreshCw, Loader2, CheckCircle2, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const DEVICE_ICONS = { mobile: Smartphone, tablette: Tablet, ordinateur: Monitor };

const PAGE_LABELS = [
    ['/sales/my-food', 'Ventes My Food'],
    ['/sales/concept-lab', 'Ventes Concept Lab'],
    ['/sales', 'Ventes Fuji Express'],
    ['/products/comparison', 'Comparaison produits'],
    ['/products', 'Produits'],
    ['/suppliers', 'Fournisseurs'],
    ['/orders', 'Commandes'],
    ['/inventory-forms', 'Formulaires inventaire'],
    ['/inventory', 'Stocks & Inventaire'],
    ['/recipes', 'Recettes'],
    ['/reception-stats', 'Stats réception'],
    ['/finance', 'Finance'],
    ['/production', 'Production'],
    ['/ratios', 'Ratios'],
    ['/pertes', 'Pertes'],
    ['/sites', 'Sites'],
    ['/users', 'Utilisateurs'],
    ['/permissions', 'Permissions'],
    ['/sync', 'Synchronisation'],
    ['/guide', 'Guide'],
    ['/', 'Tableau de bord'],
];
const pageLabel = (path) => {
    for (const [prefix, label] of PAGE_LABELS) {
        if (path === prefix || (prefix !== '/' && path.startsWith(prefix))) return label;
    }
    return path;
};

export default function LoginLogsTab({ users }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userFilter, setUserFilter] = useState('all');
    const [renameDialog, setRenameDialog] = useState(null); // { device_id, name }
    const [renaming, setRenaming] = useState(false);
    const [pageViews, setPageViews] = useState([]);
    const [days, setDays] = useState(7);
    const [showLogs, setShowLogs] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 500, days };
            if (userFilter !== 'all') params.user_id = userFilter;
            const pvParams = { days };
            if (userFilter !== 'all') pvParams.user_id = userFilter;
            const [r, pv] = await Promise.all([
                authAPI.getLoginLogs(params),
                authAPI.getPageViews(pvParams),
            ]);
            setLogs(r.data || []);
            setPageViews(pv.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de chargement du journal');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [userFilter, days]);

    const handleRename = async () => {
        setRenaming(true);
        try {
            await authAPI.renameDevice(renameDialog.device_id, renameDialog.name);
            toast.success('Appareil renommé');
            setRenameDialog(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setRenaming(false);
        }
    };

    const fmt = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        const opts = { timeZone: 'America/Martinique' };
        return d.toLocaleDateString('fr-FR', { ...opts, day: '2-digit', month: '2-digit', year: '2-digit' })
            + ' ' + d.toLocaleTimeString('fr-FR', { ...opts, hour: '2-digit', minute: '2-digit' });
    };

    const usersWithLogs = useMemo(() => (users || []).filter(u => !u.guide_only || true), [users]);

    return (
        <div className="space-y-3" data-testid="login-logs-tab">
            <div className="flex flex-wrap items-center gap-2">
                <Select value={userFilter} onValueChange={setUserFilter}>
                    <SelectTrigger className="w-56" data-testid="login-logs-user-filter">
                        <SelectValue placeholder="Tous les utilisateurs" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Tous les utilisateurs</SelectItem>
                        {usersWithLogs.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.username})</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <div className="inline-flex bg-slate-200 rounded-md p-0.5 text-[11px] gap-0.5" data-testid="login-logs-period-toggle">
                    {[[7, '7 jours'], [30, '1 mois'], [60, '2 mois'], [90, '3 mois']].map(([d, lbl]) => (
                        <button key={d} type="button" onClick={() => setDays(d)}
                            className={`px-2.5 py-1 rounded font-semibold transition-all ${days === d ? 'bg-slate-900 text-white shadow' : 'text-slate-600 hover:text-slate-900'}`}
                            data-testid={`login-logs-period-${d}`}
                        >{lbl}</button>
                    ))}
                </div>
                <Button variant="outline" size="sm" onClick={load} data-testid="login-logs-refresh-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Actualiser
                </Button>
                <span className="text-xs text-slate-400">les échecs sont aussi journalisés</span>
            </div>

            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
                <div>
                    <button type="button" onClick={() => setShowLogs(v => !v)}
                        className="w-full flex items-center gap-2 px-3 py-2 border rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                        data-testid="login-logs-collapse-btn">
                        {showLogs ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                        <span className="text-sm font-bold text-slate-800">Journal des connexions</span>
                        <span className="text-xs text-slate-400">{logs.length} connexion(s) — cliquez pour {showLogs ? 'masquer' : 'afficher'}</span>
                    </button>
                    {showLogs && (
                <div className="border rounded-lg overflow-x-auto mt-2">
                    <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                            <tr className="text-slate-500">
                                <th className="text-left px-3 py-2">Date</th>
                                <th className="text-left px-3 py-2">Utilisateur</th>
                                <th className="text-left px-3 py-2">Appareil</th>
                                <th className="text-left px-3 py-2">Système</th>
                                <th className="text-left px-3 py-2">Navigateur</th>
                                <th className="text-left px-3 py-2">Écran</th>
                                <th className="text-left px-3 py-2">IP</th>
                                <th className="text-left px-3 py-2">Appli</th>
                                <th className="text-center px-3 py-2">Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(l => {
                                const Icon = DEVICE_ICONS[l.device_type] || Monitor;
                                return (
                                    <tr key={l.id} className={`border-t ${!l.success ? 'bg-red-50/50' : ''}`} data-testid={`login-log-${l.id}`}>
                                        <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">{fmt(l.ts)}</td>
                                        <td className="px-3 py-1.5">
                                            <span className="font-medium">{l.name || l.username}</span>
                                            {l.role && <span className="text-slate-400 ml-1">({l.role})</span>}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <div className="flex items-center gap-1.5">
                                                <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                                <span className="capitalize">{l.device_type}</span>
                                                {l.device_name && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{l.device_name}</Badge>}
                                                {l.device_id && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setRenameDialog({ device_id: l.device_id, name: l.device_name || '' })}
                                                        className="text-slate-300 hover:text-slate-600"
                                                        title="Nommer cet appareil"
                                                        data-testid={`rename-device-${l.id}`}
                                                    >
                                                        <Pencil className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-1.5">{l.os}</td>
                                        <td className="px-3 py-1.5">{l.browser}</td>
                                        <td className="px-3 py-1.5 tabular-nums">{l.screen || '—'}</td>
                                        <td className="px-3 py-1.5 font-mono text-slate-500">{l.ip || '—'}</td>
                                        <td className="px-3 py-1.5">{l.app === 'guide' ? 'Guide' : 'Sokuzai'}</td>
                                        <td className="px-3 py-1.5 text-center">
                                            {l.success ? (
                                                <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" title="Connexion réussie" />
                                            ) : (
                                                <span title={l.reason}><XCircle className="w-4 h-4 text-red-500 inline" /></span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {logs.length === 0 && (
                                <tr><td colSpan={9} className="text-center py-8 text-slate-400">Aucune connexion enregistrée pour le moment.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                    )}
                </div>
            )}

            <div className="space-y-2" data-testid="page-views-section">
                <div className="flex items-center gap-2 pt-2">
                    <h3 className="text-sm font-bold text-slate-800">Pages consultées par jour</h3>
                    <span className="text-[11px] text-slate-400">(période sélectionnée, rétention 90 j)</span>
                </div>
                {pageViews.length === 0 ? (
                    <div className="text-xs text-slate-400 italic border rounded-lg px-3 py-4">
                        Aucune consultation enregistrée pour le moment (le suivi démarre avec cette mise à jour).
                    </div>
                ) : (
                    <div className="border rounded-lg overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50">
                                <tr className="text-slate-500">
                                    <th className="text-left px-3 py-2 whitespace-nowrap">Jour</th>
                                    <th className="text-left px-3 py-2">Utilisateur</th>
                                    <th className="text-left px-3 py-2">Pages consultées</th>
                                    <th className="text-right px-3 py-2 whitespace-nowrap">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageViews.map(g => (
                                    <tr key={`${g.date}-${g.user_id}`} className="border-t align-top" data-testid={`page-views-${g.date}-${g.user_id}`}>
                                        <td className="px-3 py-1.5 whitespace-nowrap tabular-nums">
                                            {new Date(g.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                        </td>
                                        <td className="px-3 py-1.5 font-medium whitespace-nowrap">{g.name || g.username}</td>
                                        <td className="px-3 py-1.5">
                                            <div className="flex flex-wrap gap-1">
                                                {g.pages.map(p => (
                                                    <span key={p.path}
                                                        className="inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700"
                                                        title={p.path}>
                                                        {pageLabel(p.path)}
                                                        {p.count > 1 && <span className="text-slate-400 tabular-nums">×{p.count}</span>}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{g.total}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <Dialog open={!!renameDialog} onOpenChange={(v) => !v && setRenameDialog(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Nommer l&rsquo;appareil</DialogTitle>
                    </DialogHeader>
                    <Input
                        placeholder="Ex : Tablette cuisine Dillon"
                        value={renameDialog?.name || ''}
                        onChange={(e) => setRenameDialog(d => ({ ...d, name: e.target.value }))}
                        data-testid="device-name-input"
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameDialog(null)}>Annuler</Button>
                        <Button onClick={handleRename} disabled={renaming} data-testid="device-name-save-btn">
                            {renaming && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
